import { environment } from 'environments/environment';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of, ReplaySubject, switchMap, take, tap } from 'rxjs';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { AuthService } from 'app/core/auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<Notification[]>(1);
  private _unreadCount: ReplaySubject<number> = new ReplaySubject<number>(0);

  /**
   * Constructor
   */
  constructor(private _httpClient: HttpClient, private _auth: AuthService) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for notifications
   */
  get notifications$(): Observable<any> {
    return this._notifications.asObservable();
  }

  get unreadCount$(): Observable<any> {
    return this._unreadCount.asObservable();
  }

  setUnreadCount(unreadCount): void {
    this._unreadCount.next(unreadCount);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get all notifications
   */
  getAll(login): Observable<any> {
    return this._httpClient.get(`${environment.api}/notificacao`).pipe(
      catchError((err) => {
        console.log(err);
        return err;
      }),
      tap((notifications) => {
        this._notifications.next(notifications);
      })
    );
  }

  /**
   * Create a notification
   *
   * @param notification
   */
  create(notification: Notification): Observable<Notification> {
    return this.notifications$.pipe(
      take(1),
      switchMap((notifications) =>
        this._httpClient.post<Notification>('api/common/notifications', { notification }).pipe(
          map((newNotification) => {
            // Update the notifications with the new notification
            this._notifications.next([...notifications, newNotification]);

            // Return the new notification from observable
            return newNotification;
          })
        )
      )
    );
  }

  /**
   * Update the notification
   *
   * @param id
   * @param notification
   */
  update(id: string, notification: Notification): Observable<Notification> {
    return this.notifications$.pipe(
      take(1),
      switchMap((notifications) =>
        this._httpClient
          .patch<Notification>('api/common/notifications', {
            id,
            notification,
          })
          .pipe(
            map((updatedNotification: Notification) => {
              // Find the index of the updated notification
              const index = notifications.findIndex((item) => item.id === id);

              // Update the notification
              notifications[index] = updatedNotification;

              // Update the notifications
              this._notifications.next(notifications);

              // Return the updated notification
              return updatedNotification;
            })
          )
      )
    );
  }

  /**
   * Delete the notification
   *
   * @param id
   */
  delete(id: string): Observable<boolean> {
    return this.notifications$.pipe(
      take(1),
      switchMap((notifications) =>
        this._httpClient.delete<boolean>('api/common/notifications', { params: { id } }).pipe(
          map((isDeleted: boolean) => {
            // Find the index of the deleted notification
            const index = notifications.findIndex((item) => item.id === id);

            // Delete the notification
            notifications.splice(index, 1);

            // Update the notifications
            this._notifications.next(notifications);

            // Return the deleted status
            return isDeleted;
          })
        )
      )
    );
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): Promise<any> {
    return new Promise((resolve, reject) => {
      this._httpClient
        .post(`${environment.api}/conversa/buscarQuatidadeNovasMensagens`, {
          login: this._auth?.userValue?.login,
        })
        .subscribe(
          (res) => resolve(res),
          (err) => reject(err)
        );
    });
  }

  getUnreadCount(): Observable<any> {
    return this._httpClient.get(`${environment.api}/conversa/buscarQuatidadeNovasMensagens`).pipe(
      catchError((err) => {
        this._unreadCount.next(0);
        return of(true);
      }),
      tap((notifications) => {
        this._unreadCount.next(notifications);
      })
    );
  }
}
